import { fuzzyAutocomplete } from './autocomplete.js';
import { hnswSearch } from '../compiler/build-vector-index.js';
import { GROUP_EXPAND } from '../common/synonyms.js';
import { phraseOverride } from '../common/overrides.js';
import { QueryContext } from './query-context.js';
import { BKTree } from './bk-tree.js';
import { STAGE_WEIGHT, clamp01, aggregate, rankPipeline, } from './pipeline.js';
import { lexicalStage, prefixStage, fuzzyStage, graphStage } from './stages.js';
export { STAGE_WEIGHT } from './pipeline.js';
/** Default global multiplier for the context boost (see {@link SearchOptions.contextWeight}). */
const DEFAULT_CONTEXT_WEIGHT = 0.4;
export class OFOSearch {
    documents;
    invertedIndex;
    trie;
    embeddings;
    hnsw;
    graph;
    fuzzyIndex;
    vectors;
    embeddingDimensions;
    docByCode;
    docsByUg;
    synonymMap;
    /** Precomputed word→docs index backing the prefix stage (see stages.ts). */
    prefixWords = [];
    prefixWordInfo = new Map();
    constructor(data) {
        this.documents = data.documents;
        this.invertedIndex = data.invertedIndex;
        // Precompute the BK-tree once over the vocabulary so the fuzzy stage can do
        // exact edit-distance retrieval without scanning every term per token.
        this.fuzzyIndex = BKTree.build(Object.keys(this.invertedIndex.terms));
        this.trie = data.trie;
        this.embeddingDimensions = data.embeddingDimensions ?? 384;
        // Curated synonym groups stay query-side only (not baked into doc text), so
        // merge them over the WordNet-derived baked map. Curated wins on overlap.
        this.synonymMap = { ...data.synonyms?.expand, ...GROUP_EXPAND };
        if (data.embeddings) {
            this.embeddings = data.embeddings.vectors;
            this.vectors = new Map();
            for (const [id, vec] of Object.entries(data.embeddings.vectors)) {
                this.vectors.set(Number(id), new Float32Array(vec));
            }
        }
        else {
            this.vectors = new Map();
        }
        if (data.hnsw) {
            this.hnsw = {
                nodes: data.hnsw.nodes.map((n) => ({
                    id: n.id,
                    level: n.level,
                    connections: new Map(Object.entries(n.connections).map(([k, v]) => [Number(k), v])),
                })),
                entryPoint: data.hnsw.entryPoint,
                maxLevel: data.hnsw.maxLevel,
                M: data.hnsw.M,
                efConstruction: data.hnsw.efConstruction,
            };
        }
        if (data.graph) {
            this.graph = data.graph;
        }
        this.docByCode = new Map();
        this.docsByUg = new Map();
        for (const doc of Object.values(this.documents)) {
            this.docByCode.set(doc.code, doc);
            const ugCode = doc.code.substring(0, 4);
            const existing = this.docsByUg.get(ugCode) ?? [];
            existing.push(doc);
            this.docsByUg.set(ugCode, existing);
        }
        // Precompute the word→docs prefix index (title > alias > task tier) so the
        // prefix stage can serve partial queries ("electr" → "Electrician") with a
        // binary-search range scan instead of re-lowercasing/splitting every doc per
        // query. Keeps the hybrid path inside the latency gate (search-speed.spec.ts).
        const addWord = (wordInfo, code, s, tier) => {
            for (const w of s.toLowerCase().split(/\s+/)) {
                if (w.length < 2)
                    continue;
                const info = wordInfo.get(w);
                if (!info) {
                    wordInfo.set(w, { codes: [code], tier });
                }
                else {
                    if (tier > info.tier)
                        info.tier = tier;
                    if (!info.codes.includes(code))
                        info.codes.push(code);
                }
            }
        };
        for (const doc of Object.values(this.documents)) {
            addWord(this.prefixWordInfo, doc.code, doc.description ?? '', 3);
            for (const a of doc.alternateTitles ?? [])
                addWord(this.prefixWordInfo, doc.code, a, 2);
            for (const t of doc.tasks ?? [])
                addWord(this.prefixWordInfo, doc.code, t, 1);
        }
        this.prefixWords = Array.from(this.prefixWordInfo.keys()).sort();
    }
    static async load(filePath) {
        const fs = await import('node:fs');
        const data = JSON.parse(fs.readFileSync(filePath, 'utf-8'));
        return new OFOSearch(data);
    }
    static loadSync(data) {
        return new OFOSearch(data);
    }
    /** Read-only index view handed to the retrieval pipeline. */
    privateView() {
        return {
            documents: this.documents,
            invertedIndex: this.invertedIndex,
            docByCode: this.docByCode,
            graph: this.graph,
            fuzzyIndex: this.fuzzyIndex,
            prefixIndex: this.prefixWords.length ? { words: this.prefixWords, wordInfo: this.prefixWordInfo } : undefined,
        };
    }
    /**
     * Hybrid query: prefix + lexical (BM25 + title-boost) + fuzzy (edit-distance) +
     * graph (related) retrieval, aggregated and reranked, with curated phrase
     * overrides pinned to the top.
     *
     * @see ../docs/search-pipeline.md — §3 (retrieval stages) for the stage list,
     *      weights, and how `context`/prefix feeds the rerank.
     */
    query(text, options = {}) {
        const { limit = 20 } = options;
        const weights = {
            vector: options.vector ?? 0.5,
            lexical: options.lexical ?? 0.3,
            graph: options.graph ?? 0.2,
        };
        const view = this.privateView();
        const ctx = QueryContext.create(text, { bm25Limit: 200, weights, limit, synonymMap: this.synonymMap }, view);
        const stages = [lexicalStage(true), prefixStage, fuzzyStage, graphStage];
        const byCode = aggregate(stages, ctx, view);
        const overrides = phraseOverride(text);
        let results = rankPipeline(ctx, view, byCode, overrides, { weights, limit, promoteExisting: true });
        // Context awareness: bias results toward occupations similar to the supplied
        // `context` keywords (e.g. a prior "Electrician" search floats "Electrician"
        // above "Electrical Engineer"). Fully offline: the context vector is derived
        // from the precomputed occupation embeddings of the keywords' best matches —
        // no runtime model inference. Falls back to lexical term overlap when the
        // index has no vectors.
        const terms = options.context;
        if (terms && terms.length > 0) {
            const ctxWeight = options.contextWeight ?? DEFAULT_CONTEXT_WEIGHT;
            const ctxVec = this.resolveContextVector(terms, view, weights);
            const pinned = results.filter((r) => r.primaryStage === 'override');
            const ranked = results.filter((r) => r.primaryStage !== 'override');
            for (const r of ranked) {
                const doc = this.docByCode.get(r.code);
                const docVec = doc ? this.vectors.get(Number(doc.id)) : undefined;
                r.contextScore = ctxVec && docVec ? this.cosine(ctxVec, docVec) : this.lexicalContextScore(doc, terms);
                r.rerankScore += ctxWeight * (r.contextScore ?? 0);
            }
            ranked.sort((a, b) => b.rerankScore - a.rerankScore);
            results = [...pinned, ...ranked];
        }
        return results;
    }
    async semanticQuery(text, k = 10) {
        if (!this.hnsw || this.vectors.size === 0) {
            throw new Error('Semantic search requires embeddings. Recompile with --no-embeddings=false.');
        }
        const { embedQuery } = await import('../compiler/generate-embeddings.js');
        const queryVec = await embedQuery(text);
        const matches = hnswSearch(this.hnsw, this.vectors, queryVec, k);
        const results = [];
        for (const m of matches) {
            const doc = this.documents[m.id];
            if (!doc)
                continue;
            const vectorScore = clamp01(m.score);
            const confidence = vectorScore;
            const relevance = vectorScore;
            results.push({
                code: doc.code,
                description: doc.description,
                majorGroup: doc.majorGroupTitle,
                minorGroup: doc.minorGroupTitle,
                skillLevel: doc.skillLevel,
                score: m.score,
                rerankScore: STAGE_WEIGHT.vector * confidence * (0.7 + 0.3 * relevance),
                confidence,
                primaryStage: 'vector',
                stages: ['vector'],
                vectorScore,
                lexicalScore: 0,
                graphScore: 0,
                fuzzyScore: 0,
                prefixScore: 0,
                tasks: doc.tasks,
                alternateTitles: doc.alternateTitles,
                generatedDescription: doc.generatedDescription,
            });
        }
        results.sort((a, b) => b.rerankScore - a.rerankScore);
        return results;
    }
    /**
     * Lightweight lexical-only query: normalised BM25 over the expanded query
     * (no title-boost, no fuzzy/graph), with overrides prepended. Used for the
     * plain lexical comparison path and quick lexical retrieval.
     */
    lexicalQuery(text, k = 20) {
        const view = this.privateView();
        const weights = { vector: 0, lexical: 1, graph: 0 };
        const ctx = QueryContext.create(text, { bm25Limit: k, weights, limit: k, synonymMap: this.synonymMap }, view);
        const stages = [lexicalStage(false)];
        const byCode = aggregate(stages, ctx, view);
        const overrides = phraseOverride(text);
        return rankPipeline(ctx, view, byCode, overrides, { weights, limit: k, promoteExisting: false });
    }
    lookup(code) {
        return this.docByCode.get(code);
    }
    /** Dot product of two unit vectors (= cosine similarity). */
    cosine(a, b) {
        let d = 0;
        const n = Math.min(a.length, b.length);
        for (let i = 0; i < n; i++)
            d += a[i] * b[i];
        return d < -1 ? -1 : d > 1 ? 1 : d;
    }
    /**
     * Derive a context vector offline: for each keyword, retrieve its best-matching
     * occupations via the existing lexical + fuzzy stages, then take the
     * weight×score-weighted mean of those occupations' precomputed embedding
     * vectors. Returns null when no vectors are available (caller falls back to a
     * lexical overlap score) or no keyword matched any occupation.
     */
    resolveContextVector(terms, view, weights) {
        if (this.vectors.size === 0)
            return null;
        const dim = this.embeddingDimensions;
        const sum = new Float32Array(dim);
        let weightSum = 0;
        for (const { term, weight } of terms) {
            if (!term.trim())
                continue;
            const tctx = QueryContext.create(term, { bm25Limit: 30, weights, limit: 30, synonymMap: this.synonymMap }, view);
            const scored = new Map();
            for (const [c, s] of lexicalStage(true).retrieve(tctx, view)) {
                scored.set(c, Math.max(scored.get(c) ?? 0, s));
            }
            for (const [c, s] of fuzzyStage.retrieve(tctx, view)) {
                scored.set(c, Math.max(scored.get(c) ?? 0, s));
            }
            for (const [code, s] of scored) {
                if (s <= 0)
                    continue;
                const doc = this.docByCode.get(code);
                const v = doc ? this.vectors.get(Number(doc.id)) : undefined;
                if (!v || v.length !== dim)
                    continue;
                const w = (weight ?? 1) * s;
                for (let i = 0; i < dim; i++)
                    sum[i] += v[i] * w;
                weightSum += w;
            }
        }
        if (weightSum === 0)
            return null;
        for (let i = 0; i < dim; i++)
            sum[i] /= weightSum;
        let norm = 0;
        for (let i = 0; i < dim; i++)
            norm += sum[i] * sum[i];
        norm = Math.sqrt(norm);
        if (norm === 0)
            return null;
        for (let i = 0; i < dim; i++)
            sum[i] /= norm;
        return sum;
    }
    /** Lexical fallback when the index has no vectors: fraction of weighted
     *  keywords whose text appears in the occupation's title/aliases/tasks. */
    lexicalContextScore(doc, terms) {
        if (!doc)
            return 0;
        const hay = `${doc.description} ${(doc.alternateTitles ?? []).join(' ')} ${(doc.tasks ?? []).join(' ')}`.toLowerCase();
        let wsum = 0;
        let msum = 0;
        for (const { term, weight } of terms) {
            const t = term.toLowerCase().trim();
            if (!t)
                continue;
            const w = weight ?? 1;
            wsum += w;
            if (hay.includes(t))
                msum += w;
        }
        return wsum === 0 ? 0 : msum / wsum;
    }
    entries() {
        return Object.values(this.documents);
    }
    /**
     * Attach precomputed embedding vectors from a raw Float32 buffer (e.g. the
     * `embeddings.f32` web bundle). Vectors are written in ascending numeric
     * document-id order, matching `Object.keys(this.documents)` sorted numerically.
     * Enables semantic / vector / hybrid search in the browser without re-embedding.
     */
    setVectorsFromFloat32(buffer) {
        const arr = buffer instanceof Float32Array ? buffer : new Float32Array(buffer);
        const ids = Object.keys(this.documents).map(Number).sort((a, b) => a - b);
        const dim = this.embeddingDimensions;
        if (arr.length !== ids.length * dim) {
            throw new Error(`Embedding buffer size mismatch: expected ${ids.length * dim}, got ${arr.length}`);
        }
        this.vectors = new Map();
        for (let i = 0; i < ids.length; i++) {
            this.vectors.set(ids[i], arr.subarray(i * dim, (i + 1) * dim));
        }
    }
    autocomplete(prefix, limit = 10) {
        return fuzzyAutocomplete(this.trie, prefix, limit);
    }
    parents(code) {
        const parts = [
            { level: 'unitGroup', len: 4 },
            { level: 'minorGroup', len: 3 },
            { level: 'subMajorGroup', len: 2 },
            { level: 'majorGroup', len: 1 },
        ];
        const result = [];
        for (const part of parts) {
            const parentCode = code.substring(0, part.len);
            const doc = this.findGroupDoc(parentCode);
            if (doc) {
                result.push({
                    code: parentCode,
                    title: doc[`${part.level}Title`],
                    level: part.level,
                });
            }
        }
        return result;
    }
    children(code) {
        const results = [];
        const targetLen = code.length;
        for (const doc of Object.values(this.documents)) {
            if (doc.code.startsWith(code) && doc.code.length > targetLen) {
                const nextLen = targetLen + 1;
                const childCode = doc.code.substring(0, nextLen);
                if (!results.find((r) => r.code === childCode)) {
                    results.push({
                        code: childCode,
                        title: this.getLevelTitle(doc, nextLen),
                        level: this.getLevelName(nextLen),
                    });
                }
            }
        }
        return results;
    }
    siblings(code) {
        const parentCode = code.substring(0, code.length - 2);
        const results = [];
        for (const doc of Object.values(this.documents)) {
            if (doc.code.startsWith(parentCode) && doc.code.length === code.length && doc.code !== code) {
                results.push({
                    code: doc.code,
                    title: doc.description,
                    level: 'occupation',
                });
            }
        }
        return results;
    }
    tasks(code) {
        return this.docByCode.get(code)?.tasks ?? [];
    }
    aliases(code) {
        return this.docByCode.get(code)?.alternateTitles ?? [];
    }
    related(code) {
        if (!this.graph)
            return [];
        const occId = `occ:${code}`;
        const neighbors = this.graph.adjacency[occId] ?? [];
        return neighbors
            .filter((n) => n.startsWith('occ:'))
            .map((n) => {
            const neighborCode = n.substring(4);
            const doc = this.docByCode.get(neighborCode);
            return {
                code: neighborCode,
                description: doc?.description ?? '',
                relationship: 'RELATED_TO',
            };
        })
            .filter((r) => r.description);
    }
    findGroupDoc(code) {
        for (const doc of Object.values(this.documents)) {
            if (doc.code.startsWith(code) && doc.code.length >= code.length) {
                return doc;
            }
        }
        return undefined;
    }
    getLevelTitle(doc, len) {
        if (len === 1)
            return doc.majorGroupTitle;
        if (len === 2)
            return doc.subMajorGroupTitle;
        if (len === 3)
            return doc.minorGroupTitle;
        if (len === 4)
            return doc.unitGroupTitle;
        return doc.description;
    }
    getLevelName(len) {
        if (len === 1)
            return 'majorGroup';
        if (len === 2)
            return 'subMajorGroup';
        if (len === 3)
            return 'minorGroup';
        if (len === 4)
            return 'unitGroup';
        return 'occupation';
    }
}
//# sourceMappingURL=index.js.map